/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useEffect, useState } from 'react'
import { Auth } from 'aws-amplify'
import { CognitoHostedUIIdentityProvider } from '@aws-amplify/auth'
import { IAuth } from '../../types/CognitoAuth'
import { useNavigate } from 'react-router'
import { checkSessionStillActive } from '../../auth/userUtils'

type AuthContextValue = {
  auth: IAuth
  setAuthData: (data: IAuth | null) => void
  signInWithGoogle: () => Promise<void>
  logOut: () => Promise<any>
  getCurrentAuthenticatedUser: () => Promise<any>
}

export const authContext = createContext<AuthContextValue>({
  auth: null as unknown as IAuth,
  setAuthData: null as unknown as (data: IAuth | null) => void,
  signInWithGoogle: null as unknown as () => Promise<void>,
  logOut: null as unknown as () => Promise<any>,
  getCurrentAuthenticatedUser: null as unknown as () => Promise<any>,
})

const CognitoContextProvider = ({ children }: { children: JSX.Element }) => {
  const [auth, setAuth] = useState({ loading: true, data: null })
  const navigate = useNavigate()

  useEffect(() => {
    verifyUser()
  }, [])

  const verifyUser = async () => {
    setAuth({ loading: true, data: null })
    try {
      const user = await getCurrentAuthenticatedUser()
      setAuthData(user)
      await checkSessionStillActive(auth, setAuthData, navigate)
    } catch (err) {
      setAuthData({
        loading: false,
        data: null,
      })
    }
  }

  const signInWithGoogle = async () => {
    await Auth.federatedSignIn({
      provider: CognitoHostedUIIdentityProvider.Google,
    })
  }

  const logOut = async () => {
    await Auth.signOut()
    setAuthData(null)
  }

  const getCurrentAuthenticatedUser = async () => {
    return await Auth.currentAuthenticatedUser()
  }

  const setAuthData = (data: any) => {
    setAuth({ loading: false, data: data })
  }

  return (
    <authContext.Provider
      value={{
        auth,
        setAuthData,
        signInWithGoogle,
        logOut,
        getCurrentAuthenticatedUser,
      }}
    >
      {children}
    </authContext.Provider>
  )
}

export default CognitoContextProvider
